import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import { lazy } from 'react'
// import PaymentPage from '@/features/payment/payment-page'
import UiExportPage from "./pages/UiExportPage";

import { BriefingProgressToast } from '@/components/briefings/BriefingProgressToast'
// Import our styles
import '@/styles/globals.css'
import { GitHubCallback } from '@/pages/GitHubCallback'
// Import our configuration and utilities
import { config, validateConfig } from '@/lib/config'
import { theme } from '@/lib/utils'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
// Import layout components
import { AppLayout } from '@/layouts/AppLayout'
import { EnterpriseLayout } from '@/layouts/EnterpriseLayout'
import { PublicLayout } from '@/layouts/PublicLayout'
import { ThemeProvider } from '@/components/providers/theme-provider'
import { AuthProvider } from '@/components/providers/auth-provider'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { OnboardingGuard } from '@/components/auth/OnboardingGuard'
import { OnboardingRedirect } from '@/components/auth/OnboardingRedirect'
import { ErrorBoundary } from '@/components/shared/error-boundary'
import { LoadingSpinner } from '@/components/shared/loading-spinner'
import { TopNavigation } from '@/components/navigation/top-navigation'
import { OnboardingFlow } from '@/features/onboarding/OnboardingFlow'

// Import page components
// const SentimentNebulaPage = lazy(
// () => import('./features/sentiment-nebula/sentiment-nebula-page')
// )
const LandingComponent = config.features.isPrelaunch
  ? React.lazy(() => import('@/features/landing/prelaunch-page'))
  : React.lazy(() => import('@/features/landing/landing-page'))

const LoginPage = React.lazy(() => import('@/features/auth/login-page'))
const RegisterPage = React.lazy(() => import('@/features/auth/register-page'))
const DashboardPage = React.lazy(() => import('@/features/dashboard/dashboard-page'))
const BriefingsPage = React.lazy(() => import('@/features/briefings/briefings-page'))
// const SourcesPage = React.lazy(() => import('@/features/sources/sources-page'))
const IntelligencePage = React.lazy(() => import('@/features/intelligence/intelligence-page'))
const VaultPage = React.lazy(() => import('@/features/vault/vault-page'))
const SettingsPage = React.lazy(() => import('@/features/settings/SettingsPage'))

// const BillingPage = React.lazy(() => import('@/features/billing/billing-page'))
const FeaturesPage = React.lazy(() => import('@/features/marketing/features-page'))
// const PricingPage = React.lazy(() => import('@/features/marketing/pricing-page'))
const AboutPage = React.lazy(() => import('@/features/marketing/about-page'))
const PrivacyPage = React.lazy(() => import('@/features/legal/privacy-page'))
const TermsPage = React.lazy(() => import('@/features/legal/terms-page'))
const SupportPage = React.lazy(() => import('@/features/support/support-page'))
const ContactPage = React.lazy(() => import('@/features/support/contact-page'))
const CategoryPreferencesPage = React.lazy(() => import('@/features/preferences/EnhancedPreferences'))

// Create QueryClient with optimal configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors
        if (error?.status === 401 || error?.status === 403) {
          return false
        }
        // Retry up to 3 times for other errors
        return failureCount < 3
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
})

// Initialize application
function initializeApp() {
  try {
    // Validate environment configuration
    validateConfig()

    // Initialize theme
    theme.init()

    // Log app initialization in development
    if (config.isDevelopment) {
      console.log('🚀 Digestr initialized successfully', {
        version: config.app.version,
        environment: config.environment,
        features: config.features,
      })
    }

    return true
  } catch (error) {
    console.error('❌ Failed to initialize app:', error)
    return false
  }
}

// App Content Component (needs to be inside Router to use useLocation)
function AppContent() {
  const location = useLocation()
  const isPrelaunchPage = location.pathname === '/'
  const isExportPage = location.pathname.startsWith('/export')
  const isPublicLayoutPage = ['/', '/features', '/pricing', '/privacy', '/terms', '/support', '/about', '/contact'].includes(location.pathname)


  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      {/* Only show TopNavigation for pages NOT using layouts */}
      {!isPrelaunchPage &&
        !isExportPage &&
        !isPublicLayoutPage &&
        !location.pathname.startsWith('/app') &&
        !location.pathname.startsWith('/enterprise') &&
        !location.pathname.startsWith('/onboarding') && (
          <TopNavigation />
        )}

      <React.Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }
      >

        <Routes>
          {/* PUBLIC ROUTES WITH LAYOUT */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingComponent />} />
            {/* <Route path="/pricing" element={<PricingPage />} /> */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/features" element={<FeaturesPage />} />
          </Route>

          {/* PUBLIC ROUTES WITHOUT LAYOUT */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/export" element={<UiExportPage />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/github/callback" element={<GitHubCallback />} />
          {/* <Route path="/app/sentiment-nebula" element={<SentimentNebulaPage />} /> */}


          {/* ONBOARDING ROUTE - At root level, wrapped with OnboardingRedirect */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <OnboardingFlow />
                </OnboardingRedirect>
              </ProtectedRoute>
            }
          />


          {/* <Route path="/payment" element={<PaymentPage />
          }
          /> */}



          {/* CLOUD APP ROUTES - Wrapped with OnboardingGuard */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout />
                </OnboardingGuard>
              </ProtectedRoute>
            }
          >


            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="briefings" element={<BriefingsPage />} />
            <Route path="briefings/:id" element={<BriefingsPage />} />
            {/* <Route path="sources" element={<SourcesPage />} /> */}
            <Route path="preferences" element={<CategoryPreferencesPage />} />

            <Route path="vault" element={<VaultPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="intelligence" element={<IntelligencePage />} />
            <Route path="settings/*" element={<SettingsPage />} />
            {/* <Route path="billing" element={<BillingPage />} /> */}
          </Route>

          {/* ENTERPRISE ROUTES - Wrapped with OnboardingGuard */}
          <Route
            path="/enterprise/*"
            element={
              <ProtectedRoute requireEnterprise={true}>
                <OnboardingGuard>
                  <EnterpriseLayout />
                </OnboardingGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/enterprise/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="intelligence" element={<IntelligencePage />} />
            <Route path="team" element={<div className="p-8">Team Management (Coming Soon)</div>} />
            <Route path="integrations" element={<div className="p-8">Integrations (Coming Soon)</div>} />
            <Route path="vault" element={<VaultPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/*" element={<SettingsPage />} />
          </Route>

          {/* OLD ROUTES - Redirect to new structure for backward compatibility */}
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/briefings" element={<Navigate to="/app/briefings" replace />} />
          <Route path="/briefings/:id" element={<Navigate to="/app/briefings" replace />} />
          <Route path="/sources" element={<Navigate to="/app/sources" replace />} />
          <Route path="/intelligence" element={<Navigate to="/app/intelligence" replace />} />
          <Route path="/vault" element={<Navigate to="/app/vault" replace />} />
          <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
          <Route path="/settings/*" element={<Navigate to="/app/settings" replace />} />
          <Route path="/billing" element={<Navigate to="/app/billing" replace />} />

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Global toast - OUTSIDE Routes */}
        <Toaster position="bottom-right" expand={false} richColors closeButton />
        <BriefingProgressToast />
      </React.Suspense>


    </div>
  )
}

// Main App Component
function App() {
  const throwError = () => {
    throw new Error("GlitchTip frontend test error from Digestr");
  };

  const [isInitialized, setIsInitialized] = React.useState(false);
  const [initError, setInitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const success = initializeApp();
    if (success) {
      setIsInitialized(true);
    } else {
      setInitError("Failed to initialize application");
    }
  }, []);

  // ✦ Initialization Error Screen
  if (!isInitialized && initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">
            Initialization Error
          </h1>
          <p className="text-muted-foreground">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>

          {/* GlitchTip Test Button */}
          {/* <button
            onClick={throwError}
            className="mt-6 px-4 py-2 bg-red-600 text-white rounded-md"
          >
            Trigger GlitchTip Error
          </button> */}
        </div>
      </div>
    );
  }

  // ✦ Initialization Loading Screen
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />

        {/* GlitchTip Test Button */}
        {/* <button
          onClick={throwError}
          className="absolute bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md"
        >
          Trigger GlitchTip Error
        </button> */}
      </div>
    );
  }

  // ✦ Main App UI (initialization complete)
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            {!isInitialized ? (
              <div className="min-h-screen flex items-center justify-center bg-background">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <Router>
                <AppContent />
              </Router>
            )}
          </AuthProvider>
        </ThemeProvider>

        {/* {config.isDevelopment && (
          <ReactQueryDevtools initialIsOpen={false} />
        )} */}
      </QueryClientProvider>
    </ErrorBoundary>
  )
    ;
}

export default App;
